import type { EnvelopeDirection } from '@const/consts';
import type {
  IEnvelopeExcelExportJobResponse,
} from '@services/envelopes';
import type { IEnvelope } from '@store/modules/envelopes/types';

export interface IEnvelopeExportTarget {
  direction: EnvelopeDirection;
  envelopeOguids?: string[];
}

export interface IWaitForEnvelopeExcelExportJobOptions {
  pollAttempts?: number;
  pollIntervalMs?: number;
  sleep?: (delay: number) => Promise<void>;
}

const EXCEL_POLL_INTERVAL_MS = 2000;
const EXCEL_POLL_ATTEMPTS = 300;
const ExcelExportDirection = {
  INCOMING: 'INCOMING' as EnvelopeDirection,
  OUTGOING: 'OUTGOING' as EnvelopeDirection,
};

const terminalStatuses = new Set<string>([
  'COMPLETED',
  'ERROR',
  'STOPPED',
  'EXPIRED',
]);

const defaultSleep = async (delay: number): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, delay));

const hasEnvelopeDirection = (
  envelope: IEnvelope,
  direction: EnvelopeDirection
): boolean => envelope.direction
  ? envelope.direction === direction
  : Boolean(envelope.directions?.includes(direction));

export const getEnvelopeExportTargets = ({
  currentDirection,
  isAllEnvelopesSelected,
  isMyTasks,
  selectedEnvelopes,
}: {
  currentDirection: EnvelopeDirection | null;
  isAllEnvelopesSelected: boolean;
  isMyTasks: boolean;
  selectedEnvelopes: IEnvelope[];
}): IEnvelopeExportTarget[] => {
  const directions = isMyTasks
    ? [ExcelExportDirection.INCOMING, ExcelExportDirection.OUTGOING]
    : currentDirection
      ? [currentDirection]
      : [];

  if (isAllEnvelopesSelected) {
    return directions.map((direction) => ({ direction }));
  }

  return directions.flatMap((direction) => {
    const envelopeOguids = Array.from(new Set(
      selectedEnvelopes
        .filter((envelope) => hasEnvelopeDirection(envelope, direction))
        .map(({ oguid }) => oguid)
        .filter(Boolean)
    ));

    return envelopeOguids.length ? [{ direction, envelopeOguids }] : [];
  });
};

export const getContentDispositionFileName = (contentDisposition?: string): string | null => {
  if (!contentDisposition) return null;

  const encodedMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) {
    try {
      return decodeURIComponent(encodedMatch[1].trim().replace(/^"|"$/g, ''));
    } catch {
      return encodedMatch[1].trim().replace(/^"|"$/g, '');
    }
  }

  const fallbackMatch = contentDisposition.match(/filename="?([^";]+)"?/i);

  return fallbackMatch?.[1] ?? null;
};

export const getContentDispositionHeader = (
  headers: Record<string, string | undefined>
): string | undefined => headers['content-disposition'] ?? headers['Content-Disposition'];

export const waitForEnvelopeExcelExportJob = async (
  initialJob: IEnvelopeExcelExportJobResponse,
  getJob: (jobOguid: string) => Promise<IEnvelopeExcelExportJobResponse>,
  {
    pollAttempts = EXCEL_POLL_ATTEMPTS,
    pollIntervalMs = EXCEL_POLL_INTERVAL_MS,
    sleep = defaultSleep,
  }: IWaitForEnvelopeExcelExportJobOptions = {}
): Promise<IEnvelopeExcelExportJobResponse> => {
  let job = initialJob;

  for (let attempt = 0; attempt <= pollAttempts; attempt += 1) {
    if (terminalStatuses.has(job.status)) return job;
    if (attempt === pollAttempts) break;

    await sleep(pollIntervalMs);
    job = await getJob(job.oguid);
  }

  throw new Error('EXCEL_EXPORT_TIMEOUT');
};
